import React, { JSX } from "react";
import { format, isPast, isToday, isTomorrow } from "date-fns";
import cx from "classnames";
import { generatePath } from "react-router-dom";
import {
  AccountType,
  DateExtendedPill,
  IInboxRowLabelProps,
} from "component-library";
import { TFunction } from "i18n";
import { checkIsAgent, getUserAttribute, UserAttributes } from "shared-auth";
import { HeapCustomEvent, HeapService } from "shared-services";
import {
  DatesFormat,
  ISelfTapeRequestStatus,
  MediaRequestType,
} from "shared-types";
import { ReactComponent as MicIcon } from "component-library/src/images/svg/icons/media-audio.svg";
import { ReactComponent as FilmCameraIcon } from "component-library/src/images/svg/icons/media-filmcamera.svg";
import PageKey from "config/PageKey";
import { ISelfTapeRequestNotification } from "models/selfTapeRequest";

import { ISelfTapeRequestNotificationsPageTranslation } from "models/translation";
import defaultsWithFallback from "helpers/defaultsWithFallback";
import { fallbackTranslation } from "services/api/fallbackData";

import { IWithSelfTapeRequestNotificationsFilterTexts } from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter";
import {
  ISelfTapeRequestNotificationTableItem,
  ISelfTapeRequestNotificationTag,
} from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.models";
import {
  BREAKDOWN_FULL_PATH,
  ROLE_FULL_PATH,
  SELF_TAPE_DETAIL_FULL_PATH,
} from "config/common";

const fallbackPageTranslation =
  fallbackTranslation[PageKey.SelfTapeRequestNotifications];

interface IPrepareTitle {
  title: string | JSX.Element;
  performerNames: string[];
  userType: AccountType;
  t: TFunction;
  mediaRequestType?: MediaRequestType;
}

export function prepareTitle({
  title,
  performerNames,
  userType,
  t,
  mediaRequestType = MediaRequestType.SelfTape,
}: IPrepareTitle): string | JSX.Element {
  const client = t("common:selfTapes.request.label.client", {
    name: performerNames.join(", "),
  });

  const requestIconsMap = {
    [MediaRequestType.SelfTape]: <FilmCameraIcon />,
    [MediaRequestType.AudioClip]: <MicIcon />,
  };

  return (
    <div className="flex gap-1">
      <span>{requestIconsMap[mediaRequestType]}</span>
      <span className="leading-5">
        {userType === AccountType.Agent ? (
          <>
            {title}
            <br />
            {client}
          </>
        ) : (
          title
        )}
      </span>
    </div>
  );
}

export function prepareTag({
  id,
  name,
  url,
  t,
}: {
  id: string;
  name: string;
  url: string;
  t: TFunction;
}): ISelfTapeRequestNotificationTag {
  return {
    name: t("common:selfTapes.request.label.breakdown", { name }),
    id,
    url,
  };
}

export function prepareFilterControlTexts(
  pageTranslation: ISelfTapeRequestNotificationsPageTranslation
): IWithSelfTapeRequestNotificationsFilterTexts {
  const texts = defaultsWithFallback(
    pageTranslation?.filter,
    fallbackPageTranslation.filter
  );

  return {
    clientDropdownPlaceholder: texts.clientDropdownPlaceholder,
    clientSearchPlaceholder: texts.clientSearchPlaceholder,
    dateReceivedInputPlaceholder: texts.dateReceivedInputPlaceholder,
  };
}

function prepareSentDate({
  date,
  isRead,
  t,
}: {
  date: string;
  isRead: boolean;
  t: TFunction;
}) {
  const expiryDate = new Date(date);

  return (
    <span className={cx({ "font-semibold": !isRead })}>
      {t("common:selfTapes.request.label.sent", {
        date: t("common:datesFormat.date.time.at", {
          date: format(expiryDate, DatesFormat.dateFormatPrimary),
          time: format(expiryDate, DatesFormat.timeFormatPrimary),
        }),
      })}
    </span>
  );
}

function prepareExpiryDate({
  date,
  isRead,
  t,
  originalExpiryDate,
}: {
  date: string;
  isRead: boolean;
  t: TFunction;
  originalExpiryDate?: string;
}) {
  const expiryDateObject = new Date(date);
  const expiryDay = format(expiryDateObject, DatesFormat.dateFormatPrimary);
  const expiryTime = format(expiryDateObject, DatesFormat.timeFormatPrimary);

  if (isPast(expiryDateObject)) {
    return (
      <span className="text-2 font-semibold text-red-cardinal">
        {t("common:selfTapes.request.expired")}
      </span>
    );
  }

  let expiryDateResult = t("common:datesFormat.date.time.at", {
    date: expiryDay,
    time: expiryTime,
  });

  if (isToday(expiryDateObject)) {
    expiryDateResult = t("common:datesFormat.date.time.at", {
      date: t("common:datesFormat.today"),
      time: expiryTime,
    });
  }

  if (isTomorrow(expiryDateObject)) {
    expiryDateResult = t("common:datesFormat.date.time.at", {
      date: t("common:datesFormat.tomorrow"),
      time: expiryTime,
    });
  }

  return (
    <DateExtendedPill
      className={cx({ "font-semibold": !isRead })}
      currentExpiryDate={t("common:selfTapes.request.label.expires", {
        date: expiryDateResult,
      })}
      originalExpiryDate={originalExpiryDate}
      expiryDate={date}
    />
  );
}

export function prepareBreakdownUrl(
  item: ISelfTapeRequestNotification,
  userType: AccountType
) {
  const breakdownId = item.breakdown.id;

  if (userType === AccountType.Performer) {
    return generatePath(ROLE_FULL_PATH, {
      roleId: item.roles[0].id,
      breakdownId,
    });
  }

  return generatePath(BREAKDOWN_FULL_PATH, { breakdownId });
}

export function getSelfTaperOpenEventName(
  isAgent: boolean,
  forwardedToPerformer: boolean
) {
  if (isAgent) {
    return HeapCustomEvent.AgentRequestReceived;
  }

  if (forwardedToPerformer) {
    return HeapCustomEvent.AgentToPerformerRequestReceived;
  }

  return HeapCustomEvent.CDToPerformerRequestReceived;
}

export function prepareSelfTapeRequestNotificationLabel({
  status,
  forwardedToPerformer,
  forwardedToTagmin,
  t,
}: {
  status: ISelfTapeRequestStatus;
  forwardedToPerformer?: boolean;
  forwardedToTagmin?: boolean;
  t: TFunction;
}): IInboxRowLabelProps[] {
  const isAgent = checkIsAgent();

  switch (status) {
    case ISelfTapeRequestStatus.Accepted: {
      const acceptedLabel = {
        text: t("common:selfTapes.request.pill.accepted"),
        className: "bg-orange-burnt py-0",
      };

      if (isAgent && forwardedToTagmin)
        return [
          acceptedLabel,
          {
            text: t("common:selfTapes.request.pill.emailedOnTagmin"),
            className: "bg-tagmin-primary py-0",
          },
        ];

      if (isAgent && forwardedToPerformer)
        return [
          acceptedLabel,
          {
            text: t("common:selfTapes.request.pill.sendToClient"),
            className: "bg-green-river py-0",
          },
        ];

      return [acceptedLabel];
    }
    case ISelfTapeRequestStatus.Rejected:
      return [
        {
          text: t("common:selfTapes.request.pill.declined"),
          className: "bg-red-cardinal py-0",
        },
      ];
    case ISelfTapeRequestStatus.ResponseReceived:
      return [
        {
          text: t("selfTapes.request.pill.responseReceived"),
          className: 'bg-green-grass py-0"',
        },
      ];
    default:
      return [];
  }
}

export function prepareSelfTapeRequestItem({
  item,
  userType,
  t,
}: {
  item: ISelfTapeRequestNotification;
  userType: AccountType;
  t: TFunction;
}): ISelfTapeRequestNotificationTableItem {
  const performerNames = item.performer.filter(Boolean).map(({ name }) => name);
  const title = prepareTitle({
    title: item.notificationData.title,
    performerNames,
    userType,
    mediaRequestType: item.notificationData.mediaRequestType,
    t,
  });
  const url = prepareBreakdownUrl(item, userType);
  const isAccepted =
    item.notificationData.status === ISelfTapeRequestStatus.Accepted;
  const isRead = Boolean(item.acknowledged);
  const forwardedToPerformer =
    !!item.notificationData?.forwardedToPerformerTimestamp;

  return {
    id: item.id,
    sender: item.castingDirector,
    title,
    tags: [
      prepareTag({ id: item.breakdown.id, name: item.breakdown.name, url, t }),
    ],
    additionalInfo: [
      prepareSentDate({
        date: item.notified.toString(),
        isRead,
        t,
      }),
      prepareExpiryDate({
        date: item.notificationData.expiryDate.toString(),
        isRead,
        t,
        originalExpiryDate: item.notificationData.originalExpiryDate,
      }),
    ],
    isRead,
    url: generatePath(SELF_TAPE_DETAIL_FULL_PATH, {
      performerRequestId: item.id,
    }),
    labels: prepareSelfTapeRequestNotificationLabel({
      status: item.notificationData.status,
      forwardedToPerformer,
      forwardedToTagmin: !!item.notificationData?.forwardedToTagminTimestamp,
      t,
    }),
    isAccepted,
    sendStats: () => {
      const isAgent = checkIsAgent();
      const { notificationData, breakdown, castingDirector } = item;

      if (!isRead) {
        HeapService.track(
          getSelfTaperOpenEventName(isAgent, forwardedToPerformer),
          {
            "Request ID": notificationData.requestId,
            "Breakdown ID": breakdown.id,
            "Agent ID": isAgent && getUserAttribute(UserAttributes.UserIdAgent),
            "Performer ID": notificationData.performerId,
            "Casting Director ID": castingDirector.id,
          }
        );
      }
    },
  };
}
